import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { db } from "./firebase";
import { useAuth } from "./auth/AuthContext";
import {
  DEFAULT_GRAPH_ID,
} from "./features/quiltGraph/graphStore";
import {
  ensureLegacyImports,
  getQuiltSessionByLegacyId,
  listTOLOQuiltSessions,
  upsertTOLOSessionFromPayload,
} from "./features/quiltGraph/migrations/importLegacy";

const STAGES = [
  {
    id: "to",
    label: "TO",
    title: "Target Outcome",
    prompt: "What exact destination are you aiming for? State it in one specific sentence.",
    helper: "Define success criteria, time horizon, and who this matters to.",
    color: "#0f766e",
  },
  {
    id: "lo",
    label: "LO",
    title: "Look & Listen",
    prompt: "What facts, constraints, and signals are present right now?",
    helper: "Separate observed facts from assumptions and unknowns.",
    color: "#0369a1",
  },
  {
    id: "po",
    label: "PO",
    title: "Possibility Moves",
    prompt: "Generate alternatives, provocations, and non-obvious options.",
    helper: "Aim for quantity first. Suspend judgment while listing options.",
    color: "#7c3aed",
  },
  {
    id: "so",
    label: "SO",
    title: "Select & Shape",
    prompt: "What option looks strongest after comparison and refinement?",
    helper: "Use impact, effort, risk, and reversibility to evaluate.",
    color: "#c2410c",
  },
  {
    id: "go",
    label: "GO",
    title: "Go Execute",
    prompt: "What are the first concrete actions, owners, and checkpoints?",
    helper: "Convert the decision into immediate tasks and follow-up cadence.",
    color: "#15803d",
  },
];

const EMPTY_RESPONSES = {
  to: "",
  lo: "",
  po: "",
  so: "",
  go: "",
};

const STAGE_EDGES = [
  ["to", "lo"],
  ["lo", "po"],
  ["po", "so"],
  ["so", "go"],
];

const WIDTH = 920;
const HEIGHT = 540;

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function normalizeResponses(value) {
  const next = { ...EMPTY_RESPONSES };
  if (!value || typeof value !== "object") return next;
  STAGES.forEach((stage) => {
    next[stage.id] = typeof value[stage.id] === "string" ? value[stage.id] : "";
  });
  return next;
}

function normalizeNodeOverrides(value) {
  if (!value || typeof value !== "object") return {};
  return Object.entries(value).reduce((acc, [id, pos]) => {
    if (!pos || typeof pos !== "object") return acc;
    const x = Number(pos.x);
    const y = Number(pos.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return acc;
    acc[id] = {
      x: clamp(x, 34, WIDTH - 34),
      y: clamp(y, 34, HEIGHT - 34),
    };
    return acc;
  }, {});
}

function parseIdeasByStage(responses) {
  const map = {};
  STAGES.forEach((stage) => {
    const raw = responses[stage.id] || "";
    const chunks = raw
      .split(/\n|;/g)
      .map((v) => v.trim())
      .filter(Boolean)
      .slice(0, 4);
    map[stage.id] = chunks.map((text, idx) => ({
      id: `${stage.id}-idea-${idx}`,
      label: text.length > 22 ? `${text.slice(0, 22)}...` : text,
      stageId: stage.id,
      fullText: text,
      isStage: false,
      color: stage.color,
      radius: 13,
    }));
  });
  return map;
}

function getBaseGraph(responses) {
  const ideasByStage = parseIdeasByStage(responses);
  const stageNodes = STAGES.map((stage, idx) => {
    const angle = (2 * Math.PI * idx) / STAGES.length - Math.PI / 2;
    const rx = WIDTH * 0.34;
    const ry = HEIGHT * 0.34;
    return {
      id: stage.id,
      label: stage.label,
      stageTitle: stage.title,
      isStage: true,
      color: stage.color,
      radius: 18,
      x: WIDTH * 0.5 + rx * Math.cos(angle),
      y: HEIGHT * 0.5 + ry * Math.sin(angle),
    };
  });

  const ideaNodes = [];
  const ideaEdges = [];
  stageNodes.forEach((stageNode, stageIdx) => {
    const stageIdeas = ideasByStage[stageNode.id] || [];
    stageIdeas.forEach((idea, ideaIdx) => {
      const angle = (2 * Math.PI * stageIdx) / STAGES.length - Math.PI / 2 + (ideaIdx - 1.5) * 0.24;
      const dist = 86 + ideaIdx * 16;
      ideaNodes.push({
        ...idea,
        x: stageNode.x + dist * Math.cos(angle),
        y: stageNode.y + dist * Math.sin(angle),
      });
      ideaEdges.push({ from: stageNode.id, to: idea.id });
    });
  });

  const chainEdges = STAGE_EDGES.map(([from, to]) => ({ from, to }));
  return {
    nodes: [...stageNodes, ...ideaNodes],
    edges: [...chainEdges, ...ideaEdges],
  };
}

function summarizeThinking(responses) {
  const to = responses.to?.trim();
  const lo = responses.lo?.trim();
  const po = responses.po?.trim();
  const so = responses.so?.trim();
  const go = responses.go?.trim();
  if (!to && !lo && !po && !so && !go) {
    return "Start by writing a clear TO statement, then build evidence and options through GO actions.";
  }
  return [
    to ? `Target: ${to}` : null,
    lo ? `Inputs: ${lo.split(/\n|;/g).map((v) => v.trim()).filter(Boolean).slice(0, 3).join(" | ")}` : null,
    po ? `Options: ${po.split(/\n|;/g).map((v) => v.trim()).filter(Boolean).slice(0, 3).join(" | ")}` : null,
    so ? `Selection: ${so}` : null,
    go ? `Execution: ${go}` : null,
  ]
    .filter(Boolean)
    .join("\n");
}

function formatUpdatedAt(value) {
  if (!value || typeof value.toDate !== "function") return "";
  return value.toDate().toLocaleString();
}

function sortByUpdatedDesc(items) {
  return [...items].sort((a, b) => {
    const left = typeof a.updatedAt?.toMillis === "function" ? a.updatedAt.toMillis() : 0;
    const right = typeof b.updatedAt?.toMillis === "function" ? b.updatedAt.toMillis() : 0;
    return right - left;
  });
}

export default function TOLOPOSOGO() {
  const { user, login } = useAuth();
  const [stageIndex, setStageIndex] = useState(0);
  const [responses, setResponses] = useState(EMPTY_RESPONSES);
  const [physicsEnabled, setPhysicsEnabled] = useState(true);
  const [nodeOverrides, setNodeOverrides] = useState({});
  const [physicsPositions, setPhysicsPositions] = useState({});
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [sessionTitle, setSessionTitle] = useState("Untitled Session");
  const [sessionState, setSessionState] = useState("Local draft");
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [useQuiltStore, setUseQuiltStore] = useState(false);
  const [dragNodeId, setDragNodeId] = useState(null);
  const [seedNonce, setSeedNonce] = useState(0);

  const rafRef = useRef(null);
  const simRef = useRef(null);
  const svgRef = useRef(null);
  const notesInputRef = useRef(null);
  const lastSavedHashRef = useRef("");
  const bootstrapRef = useRef(false);
  const nodeOverridesRef = useRef({});
  const physicsPositionsRef = useRef({});
  const pointerStateRef = useRef({ nodeId: null, startX: 0, startY: 0, didDrag: false });
  const suppressNodeClickRef = useRef(false);
  const shouldFocusNotesRef = useRef(false);

  const activeStage = STAGES[stageIndex];
  const baseGraph = useMemo(() => getBaseGraph(responses), [responses]);

  const sanitizedNodeOverrides = useMemo(() => {
    const validIds = new Set(baseGraph.nodes.map((node) => node.id));
    return Object.entries(nodeOverrides).reduce((acc, [id, pos]) => {
      if (!validIds.has(id)) return acc;
      acc[id] = pos;
      return acc;
    }, {});
  }, [nodeOverrides, baseGraph.nodes]);

  const currentPayload = useMemo(() => {
    return {
      title: (sessionTitle || "Untitled Session").trim() || "Untitled Session",
      responses,
      nodeOverrides: sanitizedNodeOverrides,
      physicsEnabled,
      stageIndex,
    };
  }, [sessionTitle, responses, sanitizedNodeOverrides, physicsEnabled, stageIndex]);

  const payloadHash = useMemo(() => JSON.stringify(currentPayload), [currentPayload]);

  const applySessionData = useCallback((sessionId, data) => {
    const nextResponses = normalizeResponses(data?.responses);
    const nextNodeOverrides = normalizeNodeOverrides(data?.nodeOverrides);
    const nextPhysics = data?.physicsEnabled !== false;
    const rawIndex = Number.parseInt(`${data?.stageIndex ?? 0}`, 10);
    const nextIndex = Number.isNaN(rawIndex) ? 0 : clamp(rawIndex, 0, STAGES.length - 1);
    const nextTitle = (data?.title || "Untitled Session").trim() || "Untitled Session";

    const loadedPayload = {
      title: nextTitle,
      responses: nextResponses,
      nodeOverrides: nextNodeOverrides,
      physicsEnabled: nextPhysics,
      stageIndex: nextIndex,
    };

    lastSavedHashRef.current = JSON.stringify(loadedPayload);
    setActiveSessionId(sessionId);
    setSessionTitle(nextTitle);
    setResponses(nextResponses);
    setNodeOverrides(nextNodeOverrides);
    setPhysicsEnabled(nextPhysics);
    setStageIndex(nextIndex);
    setPhysicsPositions({});
    setSeedNonce((value) => value + 1);
    setSessionState("Loaded");
  }, []);

  const loadSessionById = useCallback(
    async (sessionId) => {
      if (!user || !sessionId) return;
      setSessionState("Loading session...");
      try {
        if (useQuiltStore) {
          const node = await getQuiltSessionByLegacyId(DEFAULT_GRAPH_ID, user.uid, sessionId);
          if (!node) {
            setSessionState("Session not found");
            return;
          }
          applySessionData(sessionId, node.props || {});
          return;
        }
        const snap = await getDoc(doc(db, "toloposogo_sessions", sessionId));
        if (!snap.exists()) {
          setSessionState("Session not found");
          return;
        }
        applySessionData(sessionId, snap.data());
      } catch (error) {
        setSessionState(`Load failed: ${error.message}`);
      }
    },
    [user, applySessionData, useQuiltStore]
  );

  const persistPayload = useCallback(
    async (payload, manual = false) => {
      if (!user || !activeSessionId) return false;
      setSessionState("Saving...");
      try {
        if (useQuiltStore) {
          await upsertTOLOSessionFromPayload(DEFAULT_GRAPH_ID, user.uid, activeSessionId, payload);
        } else {
          await setDoc(
            doc(db, "toloposogo_sessions", activeSessionId),
            {
              ownerUid: user.uid,
              ownerEmail: user.email || "",
              ...payload,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
        }
        lastSavedHashRef.current = JSON.stringify(payload);
        setSessionState(manual ? "Saved" : "All changes saved");
        return true;
      } catch (error) {
        setSessionState(`Save failed: ${error.message}`);
        return false;
      }
    },
    [activeSessionId, user, useQuiltStore]
  );

  const createNewSession = useCallback(async () => {
    if (!user) {
      try {
        await login();
      } catch (error) {
        setSessionState(`Login failed: ${error.message}`);
      }
      return;
    }

    const initialPayload = {
      title: "Untitled Session",
      responses: { ...EMPTY_RESPONSES },
      nodeOverrides: {},
      physicsEnabled: true,
      stageIndex: 0,
    };

    setSessionState("Creating session...");
    try {
      const newId = crypto.randomUUID ? crypto.randomUUID() : `session-${Date.now()}`;
      if (useQuiltStore) {
        await upsertTOLOSessionFromPayload(DEFAULT_GRAPH_ID, user.uid, newId, initialPayload);
      } else {
        await addDoc(collection(db, "toloposogo_sessions"), {
          ownerUid: user.uid,
          ownerEmail: user.email || "",
          ...initialPayload,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      applySessionData(newId, initialPayload);
      setSessionState("Session created");
    } catch (error) {
      setSessionState(`Create failed: ${error.message}`);
    }
  }, [user, login, applySessionData, useQuiltStore]);

  useEffect(() => {
    bootstrapRef.current = false;
    if (!user) {
      setSessions([]);
      setLoadingSessions(false);
      return undefined;
    }

    setLoadingSessions(true);
    if (useQuiltStore) {
      ensureLegacyImports(DEFAULT_GRAPH_ID, user.uid)
        .then(() => listTOLOQuiltSessions(DEFAULT_GRAPH_ID, user.uid))
        .then((rows) => {
          const mapped = rows.map((item) => ({
            id: item.props?.legacySessionId || item.id,
            title: item.props?.title || item.label || "Untitled Session",
            updatedAt: item.updatedAt,
          }));
          const ordered = sortByUpdatedDesc(mapped);
          setSessions(ordered);
          setLoadingSessions(false);
          if (!bootstrapRef.current) {
            bootstrapRef.current = true;
            if (ordered[0]) {
              void loadSessionById(ordered[0].id);
            }
          }
        })
        .catch((error) => {
          setSessionState(`Session list failed: ${error.message}`);
          setLoadingSessions(false);
        });
      return undefined;
    }

    const q = query(collection(db, "toloposogo_sessions"), where("ownerUid", "==", user.uid));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = sortByUpdatedDesc(
          snap.docs.map((item) => ({
            id: item.id,
            title: item.data().title || "Untitled Session",
            updatedAt: item.data().updatedAt,
          }))
        );
        setSessions(rows);
        setLoadingSessions(false);

        if (!bootstrapRef.current) {
          bootstrapRef.current = true;
          if (rows[0]) {
            void loadSessionById(rows[0].id);
          }
        }
      },
      (error) => {
        setSessionState(`Session list failed: ${error.message}`);
        setLoadingSessions(false);
      }
    );

    return () => unsub();
  }, [user, loadSessionById, useQuiltStore]);

  useEffect(() => {
    if (!user || !activeSessionId) return undefined;
    if (payloadHash === lastSavedHashRef.current) return undefined;

    setSessionState("Unsaved changes");
    const timer = window.setTimeout(() => {
      void persistPayload(currentPayload);
    }, 900);

    return () => window.clearTimeout(timer);
  }, [user, activeSessionId, payloadHash, currentPayload, persistPayload]);

  useEffect(() => {
    nodeOverridesRef.current = nodeOverrides;
  }, [nodeOverrides]);

  useEffect(() => {
    physicsPositionsRef.current = physicsPositions;
  }, [physicsPositions]);

  useEffect(() => {
    if (!shouldFocusNotesRef.current) return;
    shouldFocusNotesRef.current = false;
    window.requestAnimationFrame(() => {
      if (notesInputRef.current?.focus) notesInputRef.current.focus();
    });
  }, [stageIndex]);

  useEffect(() => {
    if (!physicsEnabled) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      simRef.current = null;
      return undefined;
    }

    const dynamic = {};
    baseGraph.nodes.forEach((node) => {
      const prior = physicsPositionsRef.current[node.id] || nodeOverridesRef.current[node.id] || node;
      dynamic[node.id] = {
        x: prior.x,
        y: prior.y,
        vx: 0,
        vy: 0,
      };
    });

    simRef.current = dynamic;

    const centerX = WIDTH * 0.5;
    const centerY = HEIGHT * 0.5;
    const damping = 0.86;
    const springK = 0.0035;
    const repelK = 6400;
    const restLength = 110;

    const tick = () => {
      const sim = simRef.current;
      if (!sim) return;
      const ids = Object.keys(sim);

      for (let i = 0; i < ids.length; i += 1) {
        const a = sim[ids[i]];
        for (let j = i + 1; j < ids.length; j += 1) {
          const b = sim[ids[j]];
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          const d2 = dx * dx + dy * dy + 0.01;
          const dist = Math.sqrt(d2);
          const force = repelK / d2;
          const fx = (force * dx) / dist;
          const fy = (force * dy) / dist;
          a.vx -= fx;
          a.vy -= fy;
          b.vx += fx;
          b.vy += fy;
        }
      }

      baseGraph.edges.forEach((edge) => {
        const a = sim[edge.from];
        const b = sim[edge.to];
        if (!a || !b) return;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const stretch = dist - restLength;
        const force = springK * stretch;
        const fx = (force * dx) / dist;
        const fy = (force * dy) / dist;
        a.vx += fx;
        a.vy += fy;
        b.vx -= fx;
        b.vy -= fy;
      });

      ids.forEach((id) => {
        const node = sim[id];
        node.vx += (centerX - node.x) * 0.0009;
        node.vy += (centerY - node.y) * 0.0009;
        node.vx *= damping;
        node.vy *= damping;
        node.x += node.vx;
        node.y += node.vy;
        node.x = clamp(node.x, 34, WIDTH - 34);
        node.y = clamp(node.y, 34, HEIGHT - 34);
      });

      setPhysicsPositions({ ...sim });
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      simRef.current = null;
    };
  }, [physicsEnabled, baseGraph, seedNonce]);

  const graphNodes = useMemo(() => {
    if (!physicsEnabled) {
      return baseGraph.nodes.map((node) => {
        const override = nodeOverrides[node.id];
        return override ? { ...node, x: override.x, y: override.y } : node;
      });
    }

    return baseGraph.nodes.map((node) => {
      const dynamic = physicsPositions[node.id];
      const override = nodeOverrides[node.id];
      if (dynamic) return { ...node, x: dynamic.x, y: dynamic.y };
      if (override) return { ...node, x: override.x, y: override.y };
      return node;
    });
  }, [baseGraph.nodes, physicsEnabled, physicsPositions, nodeOverrides]);

  const graphNodeMap = useMemo(() => {
    return graphNodes.reduce((acc, node) => {
      acc[node.id] = node;
      return acc;
    }, {});
  }, [graphNodes]);

  const summary = useMemo(() => summarizeThinking(responses), [responses]);
  const progressValue = ((stageIndex + 1) / STAGES.length) * 100;

  const getSvgPoint = useCallback((event) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const x = ((event.clientX - rect.left) / rect.width) * WIDTH;
    const y = ((event.clientY - rect.top) / rect.height) * HEIGHT;
    return {
      x: clamp(x, 34, WIDTH - 34),
      y: clamp(y, 34, HEIGHT - 34),
    };
  }, []);

  const handlePointerDown = useCallback((event, nodeId) => {
    event.preventDefault();
    pointerStateRef.current = {
      nodeId,
      startX: event.clientX,
      startY: event.clientY,
      didDrag: false,
    };
    setDragNodeId(nodeId);
    if (event.currentTarget?.setPointerCapture) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
  }, []);

  const handlePointerMove = useCallback(
    (event) => {
      if (!dragNodeId) return;
      const point = getSvgPoint(event);
      if (!point) return;
      const pointerState = pointerStateRef.current;
      if (pointerState.nodeId === dragNodeId) {
        const moved = Math.hypot(event.clientX - pointerState.startX, event.clientY - pointerState.startY);
        if (moved > 4) {
          pointerState.didDrag = true;
        }
      }

      setNodeOverrides((prev) => ({
        ...prev,
        [dragNodeId]: point,
      }));

      if (physicsEnabled) {
        if (simRef.current?.[dragNodeId]) {
          simRef.current[dragNodeId].x = point.x;
          simRef.current[dragNodeId].y = point.y;
          simRef.current[dragNodeId].vx = 0;
          simRef.current[dragNodeId].vy = 0;
        }
        setPhysicsPositions((prev) => {
          const existing = prev[dragNodeId] || { vx: 0, vy: 0 };
          return {
            ...prev,
            [dragNodeId]: {
              ...existing,
              x: point.x,
              y: point.y,
              vx: 0,
              vy: 0,
            },
          };
        });
      }
    },
    [dragNodeId, getSvgPoint, physicsEnabled]
  );

  const handlePointerEnd = useCallback(() => {
    if (pointerStateRef.current.didDrag) {
      suppressNodeClickRef.current = true;
      window.setTimeout(() => {
        suppressNodeClickRef.current = false;
      }, 0);
    }
    pointerStateRef.current = { nodeId: null, startX: 0, startY: 0, didDrag: false };
    setDragNodeId(null);
  }, []);

  const handleNodeClick = useCallback((node) => {
    if (suppressNodeClickRef.current) return;
    const stageId = node.isStage ? node.id : node.stageId;
    if (!stageId) return;
    const idx = STAGES.findIndex((stage) => stage.id === stageId);
    if (idx < 0) return;
    shouldFocusNotesRef.current = true;
    setStageIndex(idx);
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: 6,
        background: "radial-gradient(circle at 14% 16%, #f5fff7 0, #edf6ff 40%, #fff7ed 100%)",
      }}
    >
      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Stack spacing={1}>
            <Chip
              label="Thinking Assistance Tool"
              sx={{ alignSelf: "flex-start", bgcolor: "#111827", color: "#f9fafb", fontWeight: 700 }}
            />
            <Typography variant="h3" fontWeight={800}>
              TOLOPOSOGO
            </Typography>
            <Typography color="text.secondary" sx={{ maxWidth: 940 }}>
              A De Bono-inspired direct-attention flow that guides thought from <b>TO</b> (destination) through
              evidence and options to <b>GO</b> (execution).
            </Typography>
          </Stack>

          <Paper sx={{ p: 2.5, borderRadius: 3, border: "1px solid rgba(0,0,0,0.08)" }}>
            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                <Typography variant="h6" fontWeight={800}>
                  Session Persistence
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" onClick={() => void createNewSession()}>
                    {user ? "New Session" : "Sign In to Save"}
                  </Button>
                  <Button
                    variant="contained"
                    disabled={!user || !activeSessionId || payloadHash === lastSavedHashRef.current}
                    onClick={() => void persistPayload(currentPayload, true)}
                  >
                    Save Now
                  </Button>
                </Stack>
              </Stack>
              <FormControlLabel
                control={<Switch checked={useQuiltStore} onChange={(event) => setUseQuiltStore(event.target.checked)} />}
                label={useQuiltStore ? "Quilt mode (nodes/edges)" : "Legacy mode (toloposogo_sessions)"}
              />
              <TextField
                size="small"
                label="Session Title"
                value={sessionTitle}
                disabled={!activeSessionId}
                onChange={(event) => setSessionTitle(event.target.value)}
              />
              <Typography variant="body2" color="text.secondary">
                {loadingSessions ? "Loading sessions..." : sessionState}
              </Typography>
              {!!sessions.length && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {sessions.slice(0, 12).map((session) => (
                    <Chip
                      key={session.id}
                      color={session.id === activeSessionId ? "primary" : "default"}
                      variant={session.id === activeSessionId ? "filled" : "outlined"}
                      label={`${session.title} ${formatUpdatedAt(session.updatedAt) ? `(${formatUpdatedAt(session.updatedAt)})` : ""}`}
                      onClick={() => void loadSessionById(session.id)}
                      sx={{ maxWidth: "100%" }}
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          </Paper>

          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <Card sx={{ borderRadius: 3, border: "1px solid rgba(0,0,0,0.08)" }}>
                <CardContent>
                  <Stack spacing={2.5}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {STAGES.map((stage, idx) => {
                        const selected = idx === stageIndex;
                        return (
                          <Chip
                            key={stage.id}
                            label={stage.label}
                            onClick={() => setStageIndex(idx)}
                            sx={{
                              cursor: "pointer",
                              fontWeight: 800,
                              color: selected ? "#fff" : stage.color,
                              bgcolor: selected ? stage.color : `${stage.color}1A`,
                            }}
                          />
                        );
                      })}
                    </Stack>
                    <Typography variant="h5" fontWeight={800}>
                      {activeStage.label}: {activeStage.title}
                    </Typography>
                    <Typography>{activeStage.prompt}</Typography>
                    <Typography color="text.secondary">{activeStage.helper}</Typography>
                    <TextField
                      inputRef={notesInputRef}
                      multiline
                      minRows={6}
                      label={`${activeStage.label} Notes`}
                      value={responses[activeStage.id]}
                      onChange={(event) => {
                        const next = event.target.value;
                        setResponses((prev) => ({ ...prev, [activeStage.id]: next }));
                      }}
                      placeholder="Use lines or semicolons to capture key points."
                    />
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        disabled={stageIndex === 0}
                        onClick={() => setStageIndex((prev) => Math.max(0, prev - 1))}
                      >
                        Back
                      </Button>
                      <Button
                        variant="contained"
                        disabled={stageIndex === STAGES.length - 1}
                        onClick={() => setStageIndex((prev) => Math.min(STAGES.length - 1, prev + 1))}
                        sx={{ fontWeight: 700 }}
                      >
                        Next Stage
                      </Button>
                    </Stack>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Progress: {stageIndex + 1} / {STAGES.length}
                      </Typography>
                      <Box
                        sx={{
                          mt: 0.8,
                          width: "100%",
                          height: 10,
                          borderRadius: 999,
                          bgcolor: "#e5e7eb",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            width: `${progressValue}%`,
                            height: "100%",
                            bgcolor: activeStage.color,
                            transition: "width 0.25s ease",
                          }}
                        />
                      </Box>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={7}>
              <Paper sx={{ p: 2, borderRadius: 3, border: "1px solid rgba(0,0,0,0.08)" }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                    <Typography variant="h6" fontWeight={800}>
                      TOLOPOSOGO Graph
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setNodeOverrides({});
                          setPhysicsPositions({});
                          setSeedNonce((value) => value + 1);
                        }}
                      >
                        Reset Layout
                      </Button>
                      <FormControlLabel
                        control={
                          <Switch checked={physicsEnabled} onChange={(event) => setPhysicsEnabled(event.target.checked)} />
                        }
                        label="Physics Layout"
                      />
                    </Stack>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Drag any node to reposition it. Vertices are stage and idea nodes; edges show TO→LO→PO→SO→GO flow.
                  </Typography>
                  <Box sx={{ width: "100%", overflowX: "auto" }}>
                    <svg
                      ref={svgRef}
                      width="100%"
                      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
                      role="img"
                      aria-label="TOLOPOSOGO stage and idea graph"
                      style={{ borderRadius: 12, background: "#fbfdff", border: "1px solid #dbeafe", touchAction: "none" }}
                      onPointerMove={handlePointerMove}
                      onPointerUp={handlePointerEnd}
                      onPointerCancel={handlePointerEnd}
                      onPointerLeave={handlePointerEnd}
                    >
                      {baseGraph.edges.map((edge) => {
                        const from = graphNodeMap[edge.from];
                        const to = graphNodeMap[edge.to];
                        if (!from || !to) return null;
                        const isMainChain = STAGE_EDGES.some(([a, b]) => a === edge.from && b === edge.to);
                        return (
                          <line
                            key={`${edge.from}-${edge.to}`}
                            x1={from.x}
                            y1={from.y}
                            x2={to.x}
                            y2={to.y}
                            stroke={isMainChain ? "#1d4ed8" : "#93c5fd"}
                            strokeWidth={isMainChain ? 3 : 1.8}
                            opacity={0.82}
                          />
                        );
                      })}
                      {graphNodes.map((node) => {
                        const isActive = node.id === activeStage.id;
                        return (
                          <g key={node.id}>
                            <circle
                              cx={node.x}
                              cy={node.y}
                              r={node.radius + (isActive ? 4 : 0)}
                              fill={node.isStage ? node.color : "#ffffff"}
                              stroke={node.color}
                              strokeWidth={node.isStage ? 3 : 2}
                              style={{ cursor: dragNodeId === node.id ? "grabbing" : "grab" }}
                              onPointerDown={(event) => handlePointerDown(event, node.id)}
                              onClick={() => handleNodeClick(node)}
                            />
                            <text
                              x={node.x}
                              y={node.y + 4}
                              textAnchor="middle"
                              fontSize={node.isStage ? 12 : 10}
                              fontWeight={node.isStage ? 800 : 600}
                              fill={node.isStage ? "#f8fafc" : "#334155"}
                              style={{ pointerEvents: "none", userSelect: "none" }}
                            >
                              {node.label}
                            </text>
                          </g>
                        );
                      })}
                    </svg>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          <Paper sx={{ p: 3, borderRadius: 3, border: "1px solid rgba(0,0,0,0.08)" }}>
            <Stack spacing={1.5}>
              <Typography variant="h6" fontWeight={800}>
                Draft Synthesis Output
              </Typography>
              <Divider />
              <Typography sx={{ whiteSpace: "pre-wrap" }}>{summary}</Typography>
            </Stack>
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
}
